import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";
import {
  OAUTH_CODE_VERIFIER_KEY,
  OAUTH_NONCE_KEY,
  OAUTH_STATE_KEY,
  USER_ACCESS_TOKEN_KEY,
  USER_REFRESH_TOKEN_KEY,
} from "../../../../consts/keys";
import { initiateLogin } from "@/modules/auth/auth.api";
import { getCustomerAccountConfig } from "@/modules/auth/customer-account";
import { ID_TOKEN_STORAGE_KEY } from "@/routes/auth/callback";
import { useLocale } from "@/modules/shared/hooks/useLocale";
import { deleteCookie } from "@/utils/cookies";

// ─── Context Interface ────────────────────────────────────────────────────────

interface AuthActionsContextInterface {
  /** Redirect the user to Shopify's login page (Customer Account API OAuth). */
  login: () => Promise<void>;
  /** Clear cookies + redirect to Shopify's logout URL for full SSO logout. */
  logout: () => Promise<void>;
  /** Whether an auth action is currently in progress. */
  loading: boolean;
}

const AuthActionsContext = createContext<AuthActionsContextInterface>({
  login: async () => {},
  logout: async () => {},
  loading: false,
});

export const useAuthActions = () => {
  return useContext(AuthActionsContext);
};

// ─── Provider ──────────────────────────────────────────────────────────────────

export const AuthActionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation("Navbar");
  const router = useRouter();
  const locale = useLocale();
  const [loading, setLoading] = useState(false);

  // ── Login: redirect to Shopify OAuth ─────────────────────────────────────

  const login = useCallback(async () => {
    try {
      setLoading(true);
      const { authorizeUrl } = await initiateLogin({
        data: { locale: locale === "hr" ? "hr" : "en" },
      });
      // Redirect to Shopify login page
      globalThis.location.href = authorizeUrl;
    } catch (error) {
      console.error("Failed to initiate login:", error);
      toast.error(t("loginError", "Failed to start login"), {
        position: "bottom-center",
      });
      setLoading(false);
    }
  }, [locale, t]);

  // ── Logout: clear cookies + redirect to Shopify SSO logout ────────────────

  const logout = useCallback(async () => {
    try {
      setLoading(true);

      // Clear all auth cookies client-side
      deleteCookie(USER_ACCESS_TOKEN_KEY);
      deleteCookie(USER_REFRESH_TOKEN_KEY);
      deleteCookie(OAUTH_CODE_VERIFIER_KEY);
      deleteCookie(OAUTH_STATE_KEY);
      deleteCookie(OAUTH_NONCE_KEY);

      // Read id_token from localStorage (stored during OAuth callback)
      const idToken = globalThis.localStorage.getItem(ID_TOKEN_STORAGE_KEY);
      globalThis.localStorage.removeItem(ID_TOKEN_STORAGE_KEY);

      // Invalidate router context (clears accessToken from context)
      await router.invalidate();

      toast.success(t("logoutSuccess"), { position: "bottom-center" });

      // Redirect to Shopify SSO logout
      // Use bare appUrl as post_logout_redirect_uri (no locale in path).
      // The root route's beforeLoad reads the LANGUAGE_KEY cookie and
      // redirects to the correct /$locale path automatically.
      const { shopId, postLogoutRedirectUri } = getCustomerAccountConfig();
      const logoutUrl = new URL(
        `https://shopify.com/authentication/${shopId}/logout`
      );
      logoutUrl.searchParams.set(
        "post_logout_redirect_uri",
        postLogoutRedirectUri
      );
      if (idToken) {
        logoutUrl.searchParams.set("id_token_hint", idToken);
      }
      globalThis.location.href = logoutUrl.toString();
    } catch (error) {
      console.error("Logout failed:", error);
      // Fallback: ensure access token cookie is cleared
      deleteCookie(USER_ACCESS_TOKEN_KEY);
      await router.invalidate();
      toast.error(t("logoutError", "Failed to logout"), {
        position: "bottom-center",
      });
      setLoading(false);
    }
  }, [router, t]);

  // ── Context Value ──────────────────────────────────────────────────────────

  const state = useMemo(
    () => ({
      login,
      logout,
      loading,
    }),
    [login, logout, loading]
  );

  return (
    <AuthActionsContext.Provider value={state}>
      {children}
    </AuthActionsContext.Provider>
  );
};
